// Phase SA-4/4 — Brief §03 A1: Profile header.
//
// Renders the identity + summary row at the top of Section A:
//   • Headline: 'Your Betting Intelligence Profile'  (NOT 'Slip Autopsy' — brief §02)
//   • Subline:  '{N} slips analyzed · {N} legs audited · Profile confidence: {tier}'
//   • Confidence badge (Low / Building / Strong / Expert) per total_slips
//   • Archetype chip (Value Seeker / Jackpot Hunter / …)
//   • Overall process accuracy stat — only meaningful after 10+ slips
//
// Empty state (zero autopsies): pre-populates from STRIKZA's existing
// data so the profile is never fully blank (brief N04). For now the
// pre-populated copy is static; SA-9 polish wires it to actual
// ROLLOVER chain history.

import {
  archetypeLabel,
  type BettingProfileFull,
} from "../../slips/useBettingProfile.ts";
import { aggregateUserClv, clvBadge } from "../../clv/clvBadge.ts";
import { ClvBadge } from "../../clv/ClvBadge.tsx";

export interface ProfileHeaderProps {
  profile: BettingProfileFull;
  /** Pro tier flag — currently unused in the header itself, but
   * passed through so the parent can keep one signature for all
   * profile sub-components. */
  pro?: boolean;
}

function tierLabel(tier: BettingProfileFull["profile_confidence"]): string {
  switch (tier) {
    case "low":
      return "Low";
    case "building":
      return "Building";
    case "strong":
      return "Strong";
    case "expert":
      return "Expert";
  }
}

export function ProfileHeader({ profile }: ProfileHeaderProps): JSX.Element {
  const {
    total_slips: totalSlips,
    total_legs: totalLegs,
    profile_confidence: tier,
    archetype,
    process_accuracy_pct: accuracy,
  } = profile;

  const isEmpty = totalSlips === 0;
  const showAccuracyStat = totalSlips >= 10 && accuracy != null;

  // Voulgaris E5 — account CLV badge from REAL per-bookmaker CLV.
  // Hidden until ≥20 slips & positive CLV (doc §13) — honest-empty.
  const clvAgg = aggregateUserClv(profile.bookmaker_performance);
  const badge = clvBadge({
    avgClvPct: clvAgg.avgClvPct,
    sampleSlips: totalSlips,
  });

  return (
    <div
      data-testid="sa-profile-header"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 10,
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <h2
          className="sa-section-title"
          style={{ margin: 0, fontSize: 26 }}
        >
          Your Betting Intelligence Profile
        </h2>
        <span
          className="sa-confidence-badge"
          data-tier={tier}
          data-testid="sa-confidence-badge"
        >
          {tierLabel(tier)}
        </span>
        {archetype ? (
          <span
            className="sa-archetype-chip"
            data-testid="sa-archetype-chip"
            title={`Detected archetype: ${archetypeLabel(archetype)}`}
          >
            {archetypeLabel(archetype)}
          </span>
        ) : null}
        <ClvBadge badge={badge} />
      </header>

      <div
        style={{
          fontFamily: "var(--sa-font-mono)",
          fontSize: 12,
          color: "var(--sa-fg-secondary)",
          letterSpacing: "0.06em",
        }}
        data-testid="sa-profile-subline"
      >
        {`${totalSlips} slips analyzed · ${totalLegs} legs audited · Profile confidence: ${tierLabel(tier)}`}
      </div>

      {showAccuracyStat ? (
        <div
          data-testid="sa-profile-accuracy"
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 14.5,
            color: "var(--sa-fg-primary)",
            lineHeight: 1.55,
            marginTop: 4,
          }}
        >
          Your process was correct on{" "}
          <strong
            style={{
              color: "var(--sa-accent-saffron)",
              fontFamily: "var(--sa-font-stat)",
              fontSize: 22,
              letterSpacing: "0.5px",
            }}
          >
            {`${Math.round(Number(accuracy))}%`}
          </strong>{" "}
          of lost legs — variance, not error.
        </div>
      ) : null}

      {isEmpty ? (
        <div
          data-testid="sa-profile-empty"
          style={{
            fontFamily: "var(--sa-font-body)",
            fontSize: 13.5,
            color: "var(--sa-fg-secondary)",
            lineHeight: 1.55,
            marginTop: 6,
            padding: "12px 14px",
            border: "1px dashed var(--sa-hairline-accent)",
            borderLeft: "2px solid var(--sa-accent-saffron)",
            borderRadius: 4,
            background: "var(--sa-amber-glow)",
          }}
        >
          Based on your ROLLOVER chain history: profile initialising. Upload
          your first slip to begin deep analysis.
        </div>
      ) : null}
    </div>
  );
}
